/**
 * AuthService - Handles authentication operations including token refresh
 * Based on backend integration guide for refresh token implementation
 */

import { TokenManager } from '../utils/TokenManager';
import { EnvironmentService } from './EnvironmentService';
import { logger } from '../utils/logger';

interface LoginCredentials {
  email: string;
  password: string;
}

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

interface ApiResponse<T> {
  status: string;
  data: T;
  message?: string;
}

export class AuthService {
  private static refreshPromise: Promise<boolean> | null = null;

  /**
   * Perform login and store tokens
   */
  static async login(credentials: LoginCredentials): Promise<boolean> {
    try {
      const apiUrl = EnvironmentService.getApiUrl();
      const response = await fetch(`${apiUrl}/auth/admin-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Invalid credentials');
      }

      const result = await response.json();
      
      if (result.status === 'success') {
        if (!result.data.access_token || !result.data.refresh_token) {
          throw new Error('Invalid token response: missing access or refresh token');
        }

        TokenManager.storeTokens(result.data);
        // Basic fallback identity (admin-login returns tokens only, no profile).
        TokenManager.storeUser({
          email: credentials.email,
          role: 'admin',
          is_admin: true
        });
        // Enrich with the real profile (name, avatar, id) from /users/me.
        await this.fetchAndStoreProfile();

        return true;
      } else {
        throw new Error(result.message || 'Login failed');
      }
    } catch (error) {
      logger.tokenError('Login failed', error, 'AuthService');
      throw error;
    }
  }

  /**
   * Refresh the access token using the refresh token
   * Prevents multiple simultaneous refresh calls
   */
  static async refreshAccessToken(): Promise<boolean> {
    // Prevent multiple simultaneous refresh calls
    if (this.refreshPromise) {
      logger.tokenInfo('Refresh already in progress, waiting...', undefined, 'AuthService');
      return this.refreshPromise;
    }

    this.refreshPromise = this._performRefresh();
    const result = await this.refreshPromise;
    this.refreshPromise = null;
    return result;
  }

  /**
   * Internal method to perform the actual refresh
   */
  private static async _performRefresh(): Promise<boolean> {
    try {
      const refreshToken = TokenManager.getRefreshToken();
      
      if (!refreshToken) {
        logger.tokenWarn('No refresh token available', undefined, 'AuthService');
        return false;
      }

      logger.tokenInfo('Attempting to refresh access token...', undefined, 'AuthService');

      const apiUrl = EnvironmentService.getApiUrl();
      const response = await fetch(`${apiUrl}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          logger.tokenWarn('Refresh token expired or invalid', undefined, 'AuthService');
          TokenManager.clearTokens();
          return false;
        }
        throw new Error(`Refresh failed: ${response.status}`);
      }

      const result: ApiResponse<TokenResponse> = await response.json();
      
      if (result.status === 'success') {
        // Store new token pair (old tokens are now invalid)
        TokenManager.storeTokens(result.data);
        logger.tokenInfo('Token refresh successful', undefined, 'AuthService');
        return true;
      } else {
        throw new Error(result.message || 'Refresh failed');
      }
    } catch (error) {
      logger.tokenError('Token refresh failed', error, 'AuthService');
      TokenManager.clearTokens();
      return false;
    }
  }

  /**
   * Get a valid access token, refreshing if necessary
   * Returns null if no valid token is available (triggers re-login)
   */
  static async getValidAccessToken(): Promise<string | null> {
    if (!TokenManager.hasTokens()) {
      return null;
    }

    if (TokenManager.hasValidAccessToken()) {
      return TokenManager.getAccessToken();
    }

    // Access token is expired, try to refresh
    const refreshSuccessful = await this.refreshAccessToken();

    if (refreshSuccessful) {
      return TokenManager.getAccessToken();
    }

    // Refresh failed — force re-login
    TokenManager.clearTokens();
    return null;
  }

  /**
   * Logout user and invalidate tokens on server
   */
  static async logout(): Promise<void> {
    try {
      const accessToken = TokenManager.getAccessToken();
      
      // Call server logout endpoint to invalidate tokens
      if (accessToken) {
        try {
          const apiUrl = EnvironmentService.getApiUrl();
          await fetch(`${apiUrl}/auth/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            }
          });
          logger.tokenInfo('Server logout successful', undefined, 'AuthService');
        } catch (error) {
          logger.tokenWarn('Server logout failed, proceeding with local cleanup', error, 'AuthService');
        }
      }
    } finally {
      // Always clear local tokens regardless of server response
      TokenManager.clearTokens();
      logger.tokenInfo('Logout completed', undefined, 'AuthService');
    }
  }

  /**
   * Check if user is authenticated (has valid tokens)
   */
  static isAuthenticated(): boolean {
    return TokenManager.hasTokens();
  }

  /**
   * Get current user data
   */
  static getCurrentUser(): any | null {
    return TokenManager.getUser();
  }

  /**
   * Fetch the logged-in admin's real profile from /users/me and persist it.
   * admin-login only returns tokens, so this fills in name/avatar/id.
   * Returns the stored profile, or null on failure (basic fallback stays).
   */
  static async fetchAndStoreProfile(): Promise<any | null> {
    try {
      const token = await this.getValidAccessToken();
      if (!token) return null;

      const apiUrl = EnvironmentService.getApiUrl();
      const response = await fetch(`${apiUrl}/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return null;

      const json = await response.json();
      const u = json?.data ?? json;
      if (!u) return null;

      const existing = TokenManager.getUser() || {};
      const profile = {
        ...existing,
        id: u.id,
        email: u.email ?? existing.email,
        name: u.name,
        lastname: u.lastname,
        profile_picture_url: u.profile_picture_url,
        roles: u.roles,
      };
      TokenManager.storeUser(profile);
      return profile;
    } catch (error) {
      logger.tokenError('Failed to fetch user profile', error, 'AuthService');
      return null;
    }
  }

  /**
   * Get token information for debugging
   */
  static getTokenInfo(): any {
    return TokenManager.getTokenInfo();
  }

  /**
   * Start background token refresh monitoring
   * Checks every 5 minutes and refreshes if needed
   */
  static startTokenMonitoring(): void {
    const checkInterval = 5 * 60 * 1000; // 5 minutes
    
    const checkTokens = async () => {
      if (TokenManager.hasTokens() && TokenManager.isAccessTokenExpired()) {
        logger.tokenInfo('Background token refresh triggered', undefined, 'AuthService');
        await this.refreshAccessToken();
      }
    };

    // Initial check
    setTimeout(checkTokens, 1000);
    
    // Set up interval
    setInterval(checkTokens, checkInterval);
    
    logger.tokenInfo('Token monitoring started (5-minute intervals)', undefined, 'AuthService');
  }
}

export default AuthService;